import { createRouter, createWebHistory } from 'vue-router'
import * as Sentry from "@sentry/vue";
import Login from "@/pages/student/Login.vue";
import Mission from "@/pages/student/Mission.vue";
import Work from "@/pages/student/Work.vue";
import Account from "@/pages/student/Account.vue";
import Version from "@/pages/student/Version.vue";
import Setting from "@/pages/student/Setting.vue";
import Notice from "@/pages/student/Notice.vue";
import NoticeDetail from "@/pages/student/NoticeDetail.vue";
import Chapter from "@/pages/student/Chapter.vue";
import Question from "@/pages/student/Question.vue";
import Judge from "@/pages/student/Judge.vue";
import Answer from "@/pages/student/Answer.vue";
import LearningStatus from "@/pages/student/LearningStatus.vue";
import LearningStatusDetail from "@/pages/student/LearningStatusDetail.vue";
import NotFound from "@/pages/student/NotFound.vue";
import Error from "@/pages/student/Error.vue";
import ExternalToolTest from "@/pages/student/ExternalToolTest.vue";
import CurrentHomework from "@/pages/student/CurrentHomework.vue";
import ExpiredHomework from "@/pages/student/ExpiredHomework.vue";
import { useAuthStore } from '@/store/student/auth_store';
import { useAccountStore } from '@/store/student/account_store';
import { useVersionStore } from '@/store/student/version_store';
import { useWorkStore } from '@/store/student/work_store';
import { useGlobalNavigationStore } from '@/store/student/global_navigation_store';
import { useNextGradeStore } from '@/store/student/next_grade';
import {findSectionByChapterId} from "@/util/extract-section-data"
import {h} from "vue"

// Load top page component based on homework
const TopPage = () => {
  const globalNavigationStore = useGlobalNavigationStore()
  if(globalNavigationStore.accountStatus.number_of_homeworks == 0 && globalNavigationStore.licensedSubject.length){
    return h(Mission)
  }
  else {
    return h(CurrentHomework)
  }
}
// To avoid vue warn for load functional component
TopPage.displayName = "TopPage"

// @ts-ignore
const routes: Array[] = [
  {
    path: '/student/',
    name: 'top',
    component: TopPage,
    meta: { requiresAuth: true, screenCode: 'S2-2', displayName: 'top' },
    async beforeEnter(to, from, next){
      try {
        const globalNavigationStore = useGlobalNavigationStore()
        if(globalNavigationStore.licensedSubject.length == 0) {
          await globalNavigationStore.fetchNavSubjects()
        }
        next()
      } catch (error) {
        next()
      }
    }
  },
  {
    path: '/student/mission/',
    name: 'mission',
    component: Mission,
    meta: { requiresAuth: true, screenCode: 'S2-2' },
  },
  {
    path: '/student/login/',
    name: 'login',
    component: Login,
    meta: { requiresVisitor: true, screenCode: 'S1-1' },
  },
  {
    path: '/student/account',
    name: 'account',
    component: Account,
    meta: { requiresAuth: true, screenCode: 'S6-1' },
  },
  {
    path: '/student/version',
    name: 'version',
    component: Version,
    meta: { requiresAuth: true, screenCode: 'S6-2' },
  },
  {
    path: '/student/setting',
    name: 'setting',
    component: Setting,
    meta: { requiresAuth: true, screenCode: 'S6-2' },
    beforeEnter(to, from, next){
      const accountStore = useAccountStore()
      accountStore.fetchListSettings()
      .then(()=> {
        next()
      })
      .catch(() => next())
    }
  },
  {
    path: '/student/notice/:page_number',
    name: 'notice',
    component: Notice,
    meta: { requiresAuth: true, screenCode: 'S7-1' },
    beforeEnter(to, from, next){
      // check valid params
      if(!/^\d+$/.test(to.params.page_number)){
        next({name: 'pageNotFound', replace: true})
      }else{
        next()
      }
    }
  },
  {
    path: '/student/notice/detail/:uuid',
    name: 'notice-detail',
    component: NoticeDetail,
    meta: { requiresAuth: true, screenCode: 'S7-2' },
    beforeEnter(to, from, next){
      // check valid uuid params
      if(!/^[0-9a-fA-F]{8}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{12}$/.test(to.params.uuid)){
        next({name: 'pageNotFound', replace: true})
      }else{
        next()
      }
    }
  },
  {
    path: '/student/work/:work_id',
    name: 'work',
    component: Work,
    meta: { requiresAuth: true, screenCode: 'S3-1' },
  },
  {
    path: '/student/chapter/:chapter_id',
    name: 'chapter',
    component: Chapter,
    meta: { requiresAuth: true, screenCode: 'S3-3' },
    beforeEnter(to, from, next){
      const workStore = useWorkStore()
      // 有効なパラメータではない場合エラーページを出す
      if (!/^\d+$/.test(to.params.chapter_id)) {
        next({name: "pageNotFound"})
      } else {
        let chapter_id = parseInt(to.params.chapter_id)
        // 並列処理してもいいAPIはpromise.allで扱う
        Promise.all([
          workStore.fetchLatestSection({"chapter_id": chapter_id}),
          workStore.fetchReviewTag({"chapter_id": chapter_id}),
        ]).then(() => {
          next(()=>{
            if(workStore.works.length > 0){
              const sectionData =  findSectionByChapterId(workStore.works,chapter_id)
              workStore.saveSections(sectionData);
            }
          })
        }).catch(() => {})
      }
    }
  },
  {
    path: '/student/question',
    name: 'question',
    component: Question,
    meta: { requiresAuth: true, screenCode: 'S3-7' },
    beforeEnter(to, from, next){
      const workStore = useWorkStore()
      if(workStore.isLearningPage){
        if(to.path === "/student/"){ //NFR-3175 学習中TOP画面に戻る時sentryログを送信
          let msg = `学習中TOPに戻る: From ${from.path} To ${to.path}`
          Sentry.captureMessage(msg, "warning")
        }
        next()
      }else{
        next({name: 'top', replace: true})
      }
    }
  },
  {
    path: '/student/judge',
    name: 'judge',
    component: Judge,
    meta: { requiresAuth: true, screenCode: 'S3-8' },
    beforeEnter(to, from, next){
      const workStore = useWorkStore()
      if(workStore.isLearningPage){
        if(to.path === "/student/"){ //NFR-3175 学習中TOP画面に戻る時sentryログを送信
          let msg = `学習中TOPに戻る: From ${from.path} To ${to.path}`
          Sentry.captureMessage(msg, "warning")
        }
        next()
      }else{
        next({name: 'top', replace: true})
      }
    }
  },
  {
    path: '/student/answer',
    name: 'answer',
    component: Answer,
    meta: { requiresAuth: true, screenCode: 'S3-9' },
    beforeEnter(to, from, next){
      const workStore = useWorkStore()
      if(workStore.isLearningPage){
        if(to.path === "/student/"){ //NFR-3175 学習中TOP画面に戻る時sentryログを送信
          let msg = `学習中TOPに戻る: From ${from.path} To ${to.path}`
          Sentry.captureMessage(msg, "warning")
        }
        next()
      }else{
        next({name: 'top', replace: true})
      }
    }
  },
  {
    path: '/student/learning_status',
    name: 'learning-status',
    component: LearningStatus,
    meta: { requiresAuth: true, screenCode: 'S5-1' },
  },
  {
    path: '/student/learning_status/detail',
    name: 'learning-status-detail',
    component: LearningStatusDetail,
    meta: { requiresAuth: true, screenCode: 'S5-2' },
  },
  {
    path:'/student/404',
    name: 'pageNotFound',
    component: NotFound,
    meta: { requiresAuth: true },
  },
  {
    path: '/student/error',
    name: 'error',
    component: Error,
    meta: { requiresAuth: true }
  },
  {
    path: '/student/homework',
    component: CurrentHomework,
    name: 'current-homework',
    meta: { requiresAuth: true, screenCode: 'S9-1'},
  },
  {
    path: '/student/homework/expired',
    component: ExpiredHomework,
    name: 'expired-homework',
    meta: { requiresAuth: true, screenCode: 'S9-2'},
  },
  {
    path: '/student/external_tool_test_page',
    component: ExternalToolTest,
    name: 'external-tool-test'
  },
  {
    path: '/:pathMatch(.*)*',
    component: NotFound,
    meta: { requiresAuth: true },
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes: routes,
})


router.beforeEach(async(to, from, next) => {
  const authStore = useAuthStore();
  const accountStore = useAccountStore();
  const versionStore = useVersionStore();
  const workStore = useWorkStore();
  const nextGradeStore = useNextGradeStore();
  // fetch app_version for access updated javascript
  const app_version: HTMLElement = window.document.getElementById("app_version")
  if (to.matched.some(record => record.meta.requiresAuth)) {
    // Get login status
    if(accountStore.userId == null){
      try {
        const response = await authStore.fetchAuthStatus()
        accountStore.saveUserId(response.data.id)
        // if useId then fetch data else return to login page
        if(accountStore.userId != null){
          versionStore.saveStatusDate()
          versionStore.saveAppVersion(response.data.app_version)
          await accountStore.fetchAccountProfile()
          await nextGradeStore.fetchNextGradeClassroomList()
          Sentry.setUser({ id: response.data.id });
          next()
        } else {
          Sentry.getCurrentScope().setUser(null);
          next({ name: 'login', query: { next: to.path }}) 
        }
      } catch (error) {
        Sentry.getCurrentScope().setUser(null);
        next({ name: 'login', query: { next: to.path }}) 
      } 
    } else {
      const now = new Date().toISOString().split("T")[0]
      if(versionStore.statusDate && versionStore.statusDate !== now && !workStore.isLearningMode){
        window.location.reload()
      }
      next()
    }
  } else if (to.matched.some(record => record.meta.requiresVisitor)){
    try {
      const response = await authStore.fetchAuthStatus()
      accountStore.saveUserId(response.data.id)
      // if user_id is not null then go to top page
      if(accountStore.userId != null){
        versionStore.saveStatusDate()
        versionStore.saveAppVersion(response.data.app_version)
        await accountStore.fetchAccountProfile()
        await nextGradeStore.fetchNextGradeClassroomList()
        Sentry.setUser({ id: response.data.id });
        next({name: 'top'})
      }else {
        // if not login then set sentry user null and show the visit url page
        Sentry.getCurrentScope().setUser(null);
        next() 
      }
    } catch (error) {
      Sentry.getCurrentScope().setUser(null);
      next() 
    }
  } else {
    next()
  }
  if(app_version && versionStore.appVersion && app_version.innerHTML !== versionStore.appVersion){
    if(!workStore.isLearningMode){ //NFR-3175 S3-7~S3-10学習sequenceが終わるまではアプリバージョン相違があっても再ロードしない
      Sentry.captureMessage("appバージョン相違", "info")
      window.location.reload()
    }
  }
})

export default router
